/**
 * Page Builder Version Configuration
 *
 * This module manages versioning for the page builder to ensure
 * forward compatibility as we make updates to the JSON structure.
 *
 * Version History:
 * 0.0.5 - Initial versioning system implementation
 */

// Current page builder version - update this when making breaking changes
export const CURRENT_VERSION = '0.0.5';

// The key used in section content to store version
export const VERSION_KEY = 'f-pb_version';

/**
 * Parse a semantic version string into components
 * @param {string} versionString - Version string like "1.2.3"
 * @returns {{ major: number, minor: number, patch: number }}
 */
export function parseVersion(versionString) {
  const [major, minor, patch] = versionString.split('.').map(Number);
  return { major, minor, patch };
}

/**
 * Check if a section version is compatible with the current version
 * - No version (legacy) = compatible (for backwards compatibility)
 * - Same major version or 1 major version behind = compatible
 * - More than 1 major version behind = incompatible
 *
 * @param {string|undefined} sectionVersion - The version from the section
 * @returns {boolean} - True if compatible
 */
export function isVersionCompatible(sectionVersion) {
  // No version means legacy section - always allow for backwards compatibility
  if (!sectionVersion) return true;

  try {
    const current = parseVersion(CURRENT_VERSION);
    const section = parseVersion(sectionVersion);

    // Block if more than 1 major version behind
    return (current.major - section.major) <= 1;
  } catch (error) {
    // If version parsing fails, allow the section (be lenient)
    console.warn('Failed to parse version:', sectionVersion, error);
    return true;
  }
}

/**
 * Get a user-friendly error message for version incompatibility
 * @param {string} sectionVersion - The incompatible section version
 * @param {string} sectionName - Optional section name for context
 * @returns {string} - Error message
 */
export function getVersionError(sectionVersion, sectionName = 'This section') {
  return `${sectionName} was created with page builder version ${sectionVersion}, ` +
         `which is incompatible with the current version ${CURRENT_VERSION}. ` +
         `Please contact support for migration assistance.`;
}

/**
 * Stamp a section's content with the current version
 * @param {object} content - The section content object
 * @returns {object} - Content with version added
 */
export function stampVersion(content) {
  return {
    ...content,
    [VERSION_KEY]: CURRENT_VERSION
  };
}

/**
 * Get version from section content
 * @param {object} section - The section object
 * @returns {string|undefined} - The version or undefined if not present
 */
export function getSectionVersion(section) {
  return section?.content?.[VERSION_KEY];
}

/**
 * Compare two versions
 * @param {string} v1 - First version
 * @param {string} v2 - Second version
 * @returns {number} - Negative if v1 < v2, 0 if equal, positive if v1 > v2
 */
export function compareVersions(v1, v2) {
  const p1 = parseVersion(v1);
  const p2 = parseVersion(v2);

  if (p1.major !== p2.major) return p1.major - p2.major;
  if (p1.minor !== p2.minor) return p1.minor - p2.minor;
  return p1.patch - p2.patch;
}
